import clsx from "clsx";
import { FC, useState } from "react";

import { PopOver } from "@tsmono/react/components";
import { Icon } from "@tsmono/react/icons";

import { useAppConfig, useLogDir } from "../../app_config";
import { DB_VERSION } from "../../client/database/schema";
import {
  imperativeLogData,
  useDatabaseStats,
  useLastSyncedAt,
} from "../../log_data";

import styles from "./ViewerOptionsPopover.module.css";

export interface ViewerOptionsPopoverProps {
  showing: boolean;
  setShowing: (showing: boolean) => void;
  positionEl: HTMLElement | null;
  error?: Error;
}

export const ViewerOptionsPopover: FC<ViewerOptionsPopoverProps> = ({
  showing,
  positionEl,
  setShowing,
  error,
}) => {
  const [isClearing, setIsClearing] = useState(false);
  const [clearMessage, setClearMessage] = useState<string | null>(null);
  const dbStats = useDatabaseStats();
  const lastSyncedAt = useLastSyncedAt();
  const lastSyncedTime =
    lastSyncedAt === undefined
      ? undefined
      : new Date(lastSyncedAt).toLocaleTimeString(undefined, {
          hour: "numeric",
          minute: "2-digit",
        });
  const appConfig = useAppConfig();

  const logDir = useLogDir();

  const handleClearDatabase = () => {
    setIsClearing(true);
    setClearMessage(null);

    try {
      imperativeLogData.clearData();
      setClearMessage("Database cleared successfully");
      setTimeout(() => setClearMessage(null), 3000);
    } catch (error) {
      console.error("Failed to clear database:", error);
      setClearMessage("Failed to clear database");
      setTimeout(() => setClearMessage(null), 3000);
    }
    setIsClearing(false);
  };

  return (
    <PopOver
      id={`viewer-options-filter-popover`}
      positionEl={positionEl}
      isOpen={showing}
      setIsOpen={setShowing}
      placement="auto"
      hoverDelay={-1}
      offset={[-10, 5]}
      showArrow={false}
    >
      <div className={clsx(styles.container, "text-size-smaller")}>
        {error && (
          <>
            <div
              className={clsx(
                "text-style-label",
                "text-style-secondary",
                styles.fullWidth
              )}
            >
              Background Sync
            </div>
            <div className={styles.fullWidth}>
              <Icon name="error" className={styles.syncErrorIcon} />
              Couldn&apos;t refresh.
              {lastSyncedTime && ` Showing results from ${lastSyncedTime}.`}
            </div>
            <div className={clsx(styles.fullWidth, styles.syncErrorDetail)}>
              {error.message}
            </div>
            <div className={clsx(styles.spacer)}></div>
          </>
        )}

        <div
          className={clsx(
            "text-style-label",
            "text-style-secondary",
            styles.fullWidth
          )}
        >
          Log Directory
        </div>
        <div className={clsx(styles.fullWidth, styles.fullWidthPadded)}>
          <span className={styles.logDir}>{logDir}</span>
        </div>

        <div className={clsx(styles.spacer)}></div>

        <div className={clsx("text-style-label", "text-style-secondary")}>
          Inspect Version
        </div>
        <div className={clsx()}>{appConfig.inspect_version}</div>

        {appConfig.scout_version && (
          <>
            <div className={clsx("text-style-label", "text-style-secondary")}>
              Scout Version
            </div>
            <div className={clsx()}>{appConfig.scout_version}</div>
          </>
        )}

        <div className={clsx("text-style-label", "text-style-secondary")}>
          Schema
        </div>
        <div className={clsx()}>{DB_VERSION}</div>

        <div className={clsx(styles.spacer)}></div>

        <div className={clsx("text-style-label", "text-style-secondary")}>
          Logs
        </div>
        <div className={clsx()}>{dbStats.logCount || 0}</div>

        <div className={clsx("text-style-label", "text-style-secondary")}>
          Log Previews
        </div>
        <div className={clsx()}>{dbStats.previewCount || 0}</div>

        <div className={clsx("text-style-label", "text-style-secondary")}>
          Log Details
        </div>
        <div className={clsx()}>{dbStats.detailsCount || 0}</div>

        <div className={clsx(styles.spacer)}></div>

        <div className={clsx("text-style-label", "text-style-secondary")}>
          Tools
        </div>
        <div className={clsx()}>
          {" "}
          <button
            type="button"
            onClick={handleClearDatabase}
            disabled={isClearing}
            className={clsx(
              "btn",
              "btn-tools",
              "text-size-smallest",
              styles.clearButton
            )}
          >
            {isClearing ? "Clearing..." : "Clear Local Database"}
          </button>
        </div>

        {clearMessage && (
          <div
            className={clsx(
              styles.fullWidth,
              styles.message,
              clearMessage.includes("success")
                ? styles.messageSuccess
                : styles.messageError
            )}
          >
            {clearMessage}
          </div>
        )}
      </div>
    </PopOver>
  );
};
